import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  TextInput,
  Textarea,
  // Switch,
  Stack,
  Group,
  ActionIcon,
  Table,
  Container,
  LoadingOverlay,
  Text,
  Button
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconTrash, IconPlus } from '@tabler/icons-react';

import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import Card from 'components/Card';
import SecondaryButton from 'components/SecondaryButton';
import PrimaryButton from 'components/PrimaryButton';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';

import {
  rangeTemplateService,
  RangeTemplate,
  RangeTemplateItem
} from 'services/lia/rangeTemplateService';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';

const items = [
  { title: 'Range Templates', href: '/app/lia/questions/range-templates' },
  { title: 'Edit Template', href: '' }
];

// Schema for range items
const rangeItemSchema = z.object({
  msRangeTemplateItemID: z.string().optional(),
  label: z.string().min(1, 'Label is required'),
  value: z.string().min(1, 'Value is required'),
  sequence: z.number().optional()
});

// Main form schema
const templateSchema = z.object({
  msRangeTemplateID: z.string(),
  templateName: z.string().min(1, 'Template name is required'),
  description: z.string().optional(),
  isDefault: z.boolean().default(false),
  items: z
    .array(rangeItemSchema)
    .min(2, 'At least 2 range items are required')
    .max(10, 'Maximum 10 range items allowed')
});

type FormData = z.infer<typeof templateSchema>;

const EditRangeTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('id');
  const [opened, { open, close }] = useDisclosure(false);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [template, setTemplate] = useState<RangeTemplate | null>(null);

  /* eslint-disable */
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<FormData>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      msRangeTemplateID: templateId || '',
      templateName: '',
      description: '',
      isDefault: false,
      items: []
    }
  });
  /* eslint-enable */

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'items'
  });

  // Fetch template data
  const { data: templateData, isLoading } = useQuery({
    queryKey: ['rangeTemplate', templateId],
    queryFn: () => (templateId ? rangeTemplateService.getDetail(templateId) : null),
    enabled: !!templateId,
    retry: 1
  });

  useEffect(() => {
    if (templateData) {
      if (templateData.data && typeof templateData.data === 'object') {
        setTemplate(templateData.data);
      } else {
        // Direct template object
        setTemplate(templateData as unknown as RangeTemplate);
      }
    }
  }, [templateData]);

  // Set form data from state
  useEffect(() => {
    if (template) {
      // Reset the form with data from API
      reset({
        msRangeTemplateID: template.msRangeTemplateID,
        templateName: template.templateName,
        description: template.description || '',
        isDefault: template.isDefault || false,
        items: []
      });

      // Replace items field array
      if (template.items && Array.isArray(template.items)) {
        const formattedItems = template.items.map((item: RangeTemplateItem) => ({
          msRangeTemplateItemID: item.msRangeTemplateItemID,
          label: item.label,
          value: item.value,
          sequence: item.sequence
        }));
        replace(formattedItems);
      }
    }
  }, [template, reset, replace]);

  const updateMutation = useMutation({
    mutationFn: (data: FormData) => {
      const updateDto = {
        msRangeTemplateID: data.msRangeTemplateID,
        templateName: data.templateName,
        description: data.description || '',
        isDefault: data.isDefault,
        items: data.items.map((item, index) => ({
          msRangeTemplateItemID: item.msRangeTemplateItemID,
          label: item.label,
          value: item.value,
          sequence: item.sequence || index + 1
        }))
      };

      return rangeTemplateService.update(updateDto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rangeTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['rangeTemplate', templateId] });

      navigate('/app/lia/questions/range-templates');
      showSuccessNotification({
        title: 'Updated Successfully!',
        message: 'Range template has been updated successfully'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Failed to update template',
        message: error?.message || 'Something went wrong please try again'
      });
    }
  });

  const onSubmit = (data: FormData) => {
    setFormData(data);
    open();
  };

  const handleConfirmSubmit = () => {
    if (formData) {
      updateMutation.mutate(formData);
    }
    close();
  };

  if (!templateId) {
    navigate('/app/lia/questions/range-templates');
    return null;
  }

  return (
    <Container fluid>
      <Stack gap='md'>
        <BreadcrumbsComponent items={items} />

        <Card>
          <div style={{ position: 'relative' }}>
            <LoadingOverlay visible={isLoading || !template || updateMutation.isPending} />

            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack gap='md'>
                <Controller
                  name='templateName'
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      label='Template Name'
                      placeholder='e.g., 5-Point Likert Scale'
                      required
                      error={errors.templateName?.message}
                      disabled={isLoading || !template}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name='description'
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      label='Description'
                      placeholder='Describe what this template is used for'
                      disabled={isLoading || !template}
                      {...field}
                    />
                  )}
                />

                {/* <Controller
                  name='isDefault'
                  control={control}
                  render={({ field: { value, onChange, ...rest } }) => (
                    <Switch
                      label='Set as default template'
                      description='This template will be suggested by default when creating range questions'
                      checked={value}
                      onChange={(event) => onChange(event.currentTarget.checked)}
                      disabled={isLoading || !template}
                      {...rest}
                    />
                  )}
                /> */}

                <Text fw={500} mt='md'>
                  Range Items
                </Text>
                <Text size='sm' color='dimmed'>
                  Define the options that will be available in this template. The sequence
                  determines the order of display.
                </Text>

                <Table withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Label</Table.Th>
                      <Table.Th>Value</Table.Th>
                      <Table.Th w={100}>Sequence</Table.Th>
                      <Table.Th w={40}></Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {fields.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={4} style={{ textAlign: 'center' }}>
                          No range items added yet
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      fields.map((field, index) => (
                        <Table.Tr key={field.id}>
                          <Table.Td>
                            <Controller
                              name={`items.${index}.label`}
                              control={control}
                              render={({ field }) => (
                                <TextInput
                                  placeholder='e.g., Strongly Agree'
                                  error={errors.items?.[index]?.label?.message}
                                  disabled={isLoading || !template}
                                  {...field}
                                />
                              )}
                            />
                            <Controller
                              name={`items.${index}.msRangeTemplateItemID`}
                              control={control}
                              render={({ field }) => <input type='hidden' {...field} />}
                            />
                          </Table.Td>
                          <Table.Td>
                            <Controller
                              name={`items.${index}.value`}
                              control={control}
                              render={({ field }) => (
                                <TextInput
                                  placeholder='e.g., 5'
                                  error={errors.items?.[index]?.value?.message}
                                  disabled={isLoading || !template}
                                  {...field}
                                />
                              )}
                            />
                          </Table.Td>
                          <Table.Td>
                            <Controller
                              name={`items.${index}.sequence`}
                              control={control}
                              defaultValue={index + 1}
                              render={({ field: { onChange, value, ...rest } }) => (
                                <TextInput
                                  type='number'
                                  min={1}
                                  value={value?.toString() || (index + 1).toString()}
                                  onChange={(e) => onChange(parseInt(e.target.value) || index + 1)}
                                  disabled={isLoading || !template}
                                  {...rest}
                                />
                              )}
                            />
                          </Table.Td>
                          <Table.Td>
                            <ActionIcon
                              color='red'
                              variant='subtle'
                              onClick={() => remove(index)}
                              disabled={isLoading || !template || fields.length <= 2}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </Table.Tbody>
                </Table>

                <Button
                  leftSection={<IconPlus size={16} />}
                  variant='light'
                  color='#7B2183'
                  onClick={() =>
                    append({
                      label: '',
                      value: '',
                      sequence: fields.length + 1
                    })
                  }
                  disabled={isLoading || !template}
                >
                  Add Item
                </Button>

                {errors.items?.message && (
                  <Text color='red' size='sm'>
                    {errors.items.message}
                  </Text>
                )}

                <Group justify='flex-end' mt='xl'>
                  <SecondaryButton
                    text='Cancel'
                    onClick={() => navigate('/app/lia/questions/range-templates')}
                    mr={10}
                    disabled={isLoading}
                  />
                  <PrimaryButton
                    text='Update Template'
                    type='submit'
                    disabled={isLoading || !template}
                    loading={updateMutation.isPending}
                  />
                </Group>
              </Stack>
            </form>
          </div>
        </Card>
      </Stack>

      <ModalPopupConfirmationComponent
        buttonText='Update'
        statusProcess='edit'
        opened={opened}
        open={open}
        close={close}
        onSubmit={handleConfirmSubmit}
      />
    </Container>
  );
};

export default EditRangeTemplatePage;
